// Wave TW-1.f.2 (2026-05-24) — 내부 사용자 (출품자/admin) 거래 완료 확인 페이지
// API: GET /api/transactions/pending → { items: [tx_confirm rows] }
//      POST /api/transactions/:id/confirm → { completed, data }
//
// 외부 바이어 페이지(AuctionBuyerTransactions.js) 와 동일 흐름 — 인증 미들웨어만 다름.

import React, { useState, useEffect, useCallback } from 'react';
import { API, authFetch } from '../utils/api';

function TransactionsConfirmPage(props) {
  var showToast = props.showToast || function(){};
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(true);
  var [error, setError] = useState('');
  var [busyId, setBusyId] = useState(0);

  var load = useCallback(function() {
    setLoading(true);
    setError('');
    authFetch(API + '/transactions/pending')
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d && d.success && d.data) {
          setItems(d.data.items || []);
        } else {
          setError((d && d.error) || '목록 조회 실패');
        }
        setLoading(false);
      })
      .catch(function() {
        setError('네트워크 오류');
        setLoading(false);
      });
  }, []);

  useEffect(function() { load(); }, [load]);

  // 60s polling — 외부 바이어 측 confirm 시 자동 갱신
  useEffect(function() {
    var timer = setInterval(function() {
      if (document.visibilityState === 'visible') load();
    }, 60000);
    var onVis = function() {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return function() {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  var onDispute = function(row) {
    var reason = window.prompt('분쟁 사유를 입력하세요\n\n예: 차량 상태 불일치 / 인도 지연 / 결제 분쟁 등', '');
    if (reason === null) return;
    reason = String(reason).trim();
    setBusyId(row.id);
    authFetch(API + '/transactions/' + row.id + '/dispute', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason }),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setBusyId(0);
        if (d && d.success) {
          showToast('분쟁이 접수되었습니다. 관리자가 검토합니다.', 'success');
          load();
        } else {
          showToast((d && d.error) || '분쟁 신고 실패', 'error');
        }
      })
      .catch(function() {
        setBusyId(0);
        showToast('네트워크 오류', 'error');
      });
  };

  var onConfirm = function(row) {
    if (!window.confirm('거래 완료를 확인하시겠습니까?\n양측이 모두 확인해야 알림이 종료됩니다.')) return;
    setBusyId(row.id);
    authFetch(API + '/transactions/' + row.id + '/confirm', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setBusyId(0);
        if (d && d.success) {
          showToast(d.completed ? '양측 확인 완료 — 거래 종료' : '거래 완료 확인 — 상대방 확인 대기', 'success');
          load();
        } else {
          showToast((d && d.error) || '확인 실패', 'error');
        }
      })
      .catch(function() {
        setBusyId(0);
        showToast('네트워크 오류', 'error');
      });
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }}>📋 거래 완료 확인</h2>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
          완료된 거래(경매 낙찰 / 사전조회 매입)에 대해 양측 확인을 진행합니다.
          확인 안 하면 매일 09:30 KST 에 재알림이 발송됩니다 (최대 1주일).
        </div>
      </div>

      {loading && (
        <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>로딩 중…</div>
      )}

      {!loading && error && (
        <div style={{ padding: '60px 0', textAlign: 'center' }}>
          <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>
          <button type="button" onClick={load}
            style={{ padding: '8px 16px', background: 'white', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#475569', cursor: 'pointer' }}>
            다시 시도
          </button>
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div style={{ padding: '60px 0', textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, opacity: 0.4 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 600, marginTop: 8 }}>확인 대기 중인 거래가 없습니다</div>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {items.map(function(row) {
            var sourceLabel = row.source_type === 'rfq' ? '사전조회 (RFQ)' : '경매';
            var sellerConfirmed = !!row.seller_confirmed_at;
            var buyerConfirmed = !!row.buyer_confirmed_at;
            var winnerLabel = row.winner_type === 'external_buyer'
              ? '외부 바이어 #' + (row.external_buyer_id || '-')
              : (row.winner_type === 'internal_user'
                  ? '내부 #' + (row.internal_user_id || '-')
                  : '미상');
            return (
              <div key={row.id}
                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1B3A5C', background: '#eff6ff', padding: '3px 8px', borderRadius: 6 }}>
                    {sourceLabel} #{row.source_id}
                  </span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>
                    {(row.created_at || '').slice(0, 10)}
                  </span>
                </div>
                {row.amount && (
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                    {Number(row.amount).toLocaleString()}원
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>낙찰자: {winnerLabel}</div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, marginBottom: 12 }}>
                  <span style={{ color: sellerConfirmed ? '#15803d' : '#94a3b8' }}>
                    {sellerConfirmed ? '✓ 출품자 확인' : '⏳ 출품자 미확인'}
                  </span>
                  <span style={{ color: buyerConfirmed ? '#15803d' : '#94a3b8' }}>
                    {buyerConfirmed ? '✓ 바이어 확인' : '⏳ 바이어 미확인'}
                  </span>
                </div>
                {row.reminder_count > 0 && (
                  <div style={{ fontSize: 11, color: '#b45309', background: '#fef3c7', padding: '4px 8px', borderRadius: 6, marginBottom: 10 }}>
                    재알림 {row.reminder_count}회 발송됨
                  </div>
                )}
                {/* Wave TW-1.f.8 — 분쟁 신고된 거래는 뱃지 표시, 버튼 모두 숨김 */}
                {row.disputed_at ? (
                  <div style={{ padding: '8px 12px', background: '#fee2e2', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#991b1b' }}>
                    🚨 분쟁 신고됨 ({(row.dispute_party === 'seller' ? '출품자' : '바이어')} 신고)
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button"
                      disabled={sellerConfirmed || busyId === row.id}
                      onClick={function() { onConfirm(row); }}
                      style={{
                        flex: 2, padding: '10px 0',
                        background: sellerConfirmed ? '#e2e8f0' : '#1B3A5C',
                        color: sellerConfirmed ? '#64748b' : 'white',
                        border: 'none', borderRadius: 8,
                        fontSize: 13, fontWeight: 700,
                        cursor: sellerConfirmed ? 'default' : 'pointer',
                      }}>
                      {sellerConfirmed ? '확인 완료' : (busyId === row.id ? '처리 중…' : '거래 완료 확인')}
                    </button>
                    <button type="button"
                      disabled={busyId === row.id}
                      onClick={function() { onDispute(row); }}
                      style={{
                        flex: 1, padding: '10px 0',
                        background: 'white', color: '#dc2626',
                        border: '1.5px solid #fecaca', borderRadius: 8,
                        fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      }}>
                      🚨 분쟁 신고
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TransactionsConfirmPage;
